import React, { useEffect, useState, createElement } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { ShieldIcon, LockIcon, KeyIcon, SmartphoneIcon, AlertCircleIcon, CheckCircleIcon, EyeIcon, EyeOffIcon, RefreshCwIcon, LogOutIcon, SaveIcon, MonitorIcon, CheckIcon, XIcon, InfoIcon, CopyIcon, DownloadIcon, ScanIcon, ClipboardIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier, TotpMultiFactorGenerator, TotpSecret, getMultiFactorResolver } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc, collection, addDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { QRCodeSVG } from 'qrcode.react';
import { v4 as uuidv4 } from 'uuid';
import crypto from 'crypto-js';
import { DeleteAccountModal } from '../DeleteAccountModal';
// Feature flag for 2FA - set to false to hide the feature
const ENABLE_2FA = false;
// Interface for security settings
interface SecuritySettings {
  securityAlerts: boolean;
  emailNotifications: boolean;
  twoFactorEnabled: boolean;
  autoSignOut: boolean;
  passwordChangeReminder: boolean;
  passwordResetRequired?: boolean;
  lastPasswordChange?: any;
}
// Interface for backup code
interface BackupCode {
  id: string;
  code: string;
  used: boolean;
}
export const AccountSecurityPage = () => {
  const {
    currentUser,
    userType
  } = useAuth();
  const {
    theme
  } = useTheme();
  const darkMode = theme === 'dark';
  // Check if user has password provider (not OAuth-only)
  const [hasPasswordProvider, setHasPasswordProvider] = useState(true);
  // Password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Password visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    general: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  // Toggle states with loading and error handling
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [securityAlerts, setSecurityAlerts] = useState(true);
  // Security settings toggles
  const [autoSignOut, setAutoSignOut] = useState(true);
  const [passwordChangeReminder, setPasswordChangeReminder] = useState(false);
  // Loading and error states for security settings
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [settingsError, setSettingsError] = useState('');
  const [isSavingSettings, setIsSavingSettings] = useState({
    twoFactorEnabled: false,
    emailNotifications: false,
    securityAlerts: false,
    autoSignOut: false,
    passwordChangeReminder: false
  });
  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: 'Too Short',
    color: 'bg-gray-300'
  });
  // Password requirements state
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false
  });
  // Confirmation dialog state
  const [showSignOutConfirmation, setShowSignOutConfirmation] = useState(false);
  // Delete account confirmation state
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  // 2FA states
  const [enrolling2FA, setEnrolling2FA] = useState(false);
  const [verificationStep, setVerificationStep] = useState(1);
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [backupCodes, setBackupCodes] = useState<BackupCode[]>([]);
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [generatingBackupCodes, setGeneratingBackupCodes] = useState(false);
  // Add state for feedback options
  const [deletionFeedback, setDeletionFeedback] = useState({
    noLongerTeaching: false,
    foundAnotherPlatform: false,
    tooExpensive: false,
    technicalIssues: false,
    preferNotToSay: false,
    other: false
  });
  const [otherFeedbackText, setOtherFeedbackText] = useState('');
  // Check if user has password provider on mount
  useEffect(() => {
    if (currentUser) {
      const hasPassword = currentUser.providerData.some(provider => provider.providerId === 'password');
      setHasPasswordProvider(hasPassword);
    }
  }, [currentUser]);
  // Fetch security settings from Firestore when component mounts
  useEffect(() => {
    if (currentUser) {
      fetchSecuritySettings();
    }
  }, [currentUser]);
  // Fetch security settings from Firestore
  const fetchSecuritySettings = async () => {
    if (!currentUser) return;
    setIsLoadingSettings(true);
    setSettingsError('');
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.securitySettings) {
          const settings = userData.securitySettings;
          setTwoFactorEnabled(settings.twoFactorEnabled || false);
          setEmailNotifications(settings.emailNotifications !== false);
          setSecurityAlerts(settings.securityAlerts !== false);
          setAutoSignOut(settings.autoSignOut !== false);
          setPasswordChangeReminder(settings.passwordChangeReminder !== false);
        }
      }
    } catch (error) {
      console.error('Error fetching security settings:', error);
      setSettingsError('Failed to load security settings. Please refresh the page.');
    } finally {
      setIsLoadingSettings(false);
    }
  };
  // Update security setting in Firestore with optimistic update
  const updateSecuritySetting = async (setting: keyof SecuritySettings, value: boolean) => {
    if (!currentUser) {
      setSettingsError('You must be logged in to change security settings');
      return;
    }
    console.log(`Updating security setting: ${setting} to ${value}`);
    if (setting === 'twoFactorEnabled' && value === true) {
      console.log('Starting 2FA enrollment process');
      startTwoFactorEnrollment();
      return;
    }
    if (setting === 'twoFactorEnabled' && value === false) {
      setShowDisableConfirmation(true);
      return;
    }
    const previousValue = setting === 'twoFactorEnabled' ? twoFactorEnabled : setting === 'emailNotifications' ? emailNotifications : setting === 'securityAlerts' ? securityAlerts : setting === 'autoSignOut' ? autoSignOut : passwordChangeReminder;
    setIsSavingSettings(prev => ({
      ...prev,
      [setting]: true
    }));
    if (setting === 'twoFactorEnabled') {
      setTwoFactorEnabled(value);
    } else if (setting === 'emailNotifications') {
      setEmailNotifications(value);
    } else if (setting === 'securityAlerts') {
      setSecurityAlerts(value);
    } else if (setting === 'autoSignOut') {
      setAutoSignOut(value);
    } else if (setting === 'passwordChangeReminder') {
      setPasswordChangeReminder(value);
    }
    try {
      const securitySettings: SecuritySettings = {
        twoFactorEnabled,
        emailNotifications,
        securityAlerts,
        autoSignOut,
        passwordChangeReminder,
        [setting]: value
      };
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, {
        securitySettings,
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
      setSettingsError('');
      setSuccessMessage('Settings updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(`Error updating ${setting}:`, error);
      if (setting === 'twoFactorEnabled') {
        setTwoFactorEnabled(previousValue);
      } else if (setting === 'emailNotifications') {
        setEmailNotifications(previousValue);
      } else if (setting === 'securityAlerts') {
        setSecurityAlerts(previousValue);
      } else if (setting === 'autoSignOut') {
        setAutoSignOut(previousValue);
      } else if (setting === 'passwordChangeReminder') {
        setPasswordChangeReminder(previousValue);
      }
      setSettingsError(`Failed to update settings. Please try again.`);
    } finally {
      setIsSavingSettings(prev => ({
        ...prev,
        [setting]: false
      }));
    }
  };
  // Start two-factor authentication enrollment - SIMPLIFIED
  const startTwoFactorEnrollment = async () => {
    console.log('Starting 2FA enrollment');
    if (!currentUser) {
      setSettingsError('You must be logged in to enable 2FA');
      return;
    }
    setEnrolling2FA(true);
    setVerificationStep(1);
    setVerificationError('');
    try {
      // Use currentUser directly - Firebase SDK should handle this correctly
      const multiFactorUser = multiFactor(currentUser);
      const session = await multiFactorUser.getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
      setTotpSecret(totpSecret);
      const appName = 'MaestroConnect';
      const qrUrl = totpSecret.generateQrCodeUrl(currentUser.email || 'user', appName);
      setQrCodeUrl(qrUrl);
    } catch (error) {
      console.error('Error starting 2FA enrollment:', error);
      let errorMessage = 'Failed to start 2FA enrollment. Please try logging out and back in, then try again.';
      if (error instanceof Error) {
        console.log('Error details:', error.message);
      }
      setVerificationError(errorMessage);
      setSettingsError(errorMessage);
      setEnrolling2FA(false);
    }
  };
  // Verify TOTP code and complete enrollment
  const verifyTotpAndEnroll = async () => {
    if (!currentUser || !totpSecret) {
      setVerificationError('Something went wrong. Please try again.');
      return;
    }
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Please enter a valid 6-digit verification code.');
      return;
    }
    setIsSavingSettings(prev => ({
      ...prev,
      twoFactorEnabled: true
    }));
    try {
      const multiFactorUser = multiFactor(currentUser);
      const credential = await TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);
      await multiFactorUser.enroll(credential, 'Authenticator App');
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, {
        securitySettings: {
          twoFactorEnabled: true,
          emailNotifications,
          securityAlerts,
          autoSignOut,
          passwordChangeReminder
        },
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
      setTwoFactorEnabled(true);
      setVerificationStep(3);
      generateBackupCodes();
    } catch (error: any) {
      console.error('Error verifying TOTP:', error);
      if (error.code === 'auth/invalid-verification-code') {
        setVerificationError('Invalid verification code. Please try again.');
      } else {
        setVerificationError('Failed to verify code. Please try again.');
      }
    } finally {
      setIsSavingSettings(prev => ({
        ...prev,
        twoFactorEnabled: false
      }));
    }
  };
  // Generate backup codes
  const generateBackupCodes = async () => {
    if (!currentUser) return;
    setGeneratingBackupCodes(true);
    try {
      const codes: BackupCode[] = [];
      for (let i = 0; i < 10; i++) {
        const code = `${Math.random().toString(36).substring(2, 4).toUpperCase()}${Math.random().toString(36).substring(2, 4).toUpperCase()}-${Math.random().toString(36).substring(2, 4).toUpperCase()}${Math.random().toString(36).substring(2, 4).toUpperCase()}`;
        codes.push({
          id: uuidv4(),
          code,
          used: false
        });
      }
      const backupCodesCollectionRef = collection(db, `users/${currentUser.uid}/backupCodes`);
      const existingCodesQuery = query(backupCodesCollectionRef);
      const existingCodes = await getDocs(existingCodesQuery);
      const deletePromises = existingCodes.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      const addPromises = codes.map(code => {
        const hashedCode = crypto.SHA256(code.code).toString();
        return addDoc(backupCodesCollectionRef, {
          id: code.id,
          code: hashedCode,
          used: false,
          createdAt: serverTimestamp()
        });
      });
      await Promise.all(addPromises);
      setBackupCodes(codes);
      setShowBackupCodes(true);
    } catch (error) {
      console.error('Error generating backup codes:', error);
      setVerificationError('Failed to generate backup codes. Please try again.');
    } finally {
      setGeneratingBackupCodes(false);
    }
  };
  // Disable two-factor authentication
  const disableTwoFactorAuth = async () => {
    if (!currentUser) {
      setSettingsError('You must be logged in to disable 2FA');
      return;
    }
    setIsSavingSettings(prev => ({
      ...prev,
      twoFactorEnabled: true
    }));
    try {
      const multiFactorUser = multiFactor(currentUser);
      const enrolledFactors = multiFactorUser.enrolledFactors;
      if (enrolledFactors.length > 0) {
        for (const factor of enrolledFactors) {
          await multiFactorUser.unenroll(factor);
        }
      }
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, {
        securitySettings: {
          twoFactorEnabled: false,
          emailNotifications,
          securityAlerts,
          autoSignOut,
          passwordChangeReminder
        },
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
      setTwoFactorEnabled(false);
      setShowDisableConfirmation(false);
      setSuccessMessage('Two-factor authentication has been disabled.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      setSettingsError('Failed to disable two-factor authentication. Please try again.');
    } finally {
      setIsSavingSettings(prev => ({
        ...prev,
        twoFactorEnabled: false
      }));
    }
  };
  // Close 2FA enrollment modal
  const closeEnrollment = () => {
    setEnrolling2FA(false);
    setVerificationStep(1);
    setVerificationCode('');
    setVerificationError('');
    setTotpSecret(null);
    setQrCodeUrl('');
  };
  // Copy backup codes to clipboard
  const copyBackupCodes = () => {
    if (!backupCodes.length) return;
    const formattedCodes = backupCodes.map(code => code.code).join('\n');
    navigator.clipboard.writeText(formattedCodes);
    setSuccessMessage('Backup codes copied to clipboard!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };
  // Download backup codes as text file
  const downloadBackupCodes = () => {
    if (!backupCodes.length) return;
    const formattedCodes = backupCodes.map(code => code.code).join('\n');
    const blob = new Blob([formattedCodes], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  // Calculate password strength when password changes
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(newPassword));
    checkPasswordRequirements(newPassword);
  }, [newPassword]);
  // Password strength calculation function
  const calculatePasswordStrength = (password: string) => {
    if (!password || password.length < 1) {
      return {
        score: 0,
        label: 'Too Short',
        color: 'bg-gray-300'
      };
    }
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 0.5;
    if (/[A-Z]/.test(password)) score += 0.5;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password)) score += 1;
    score = Math.min(4, score);
    const strengthMap = [{
      score: 0,
      label: 'Too Short',
      color: 'bg-gray-300'
    }, {
      score: 1,
      label: 'Weak',
      color: 'bg-red-500'
    }, {
      score: 2,
      label: 'Fair',
      color: 'bg-orange-500'
    }, {
      score: 3,
      label: 'Good',
      color: 'bg-yellow-500'
    }, {
      score: 4,
      label: 'Strong',
      color: 'bg-green-500'
    }];
    return strengthMap[Math.floor(score)];
  };
  // Check password requirements
  const checkPasswordRequirements = (password: string) => {
    setPasswordRequirements({
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password)
    });
  };
  // Validate the password change form
  const validatePasswordForm = () => {
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      general: ''
    };
    let isValid = true;
    if (!currentPassword) {
      errors.currentPassword = 'Current password is required';
      isValid = false;
    }
    if (!newPassword) {
      errors.newPassword = 'New password is required';
      isValid = false;
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
      isValid = false;
    } else if (!passwordRequirements.lowercase || !passwordRequirements.uppercase || !passwordRequirements.number || !passwordRequirements.special) {
      errors.newPassword = 'Passwords must include lowercase, uppercase, number, and special characters.';
      isValid = false;
    }
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (confirmPassword !== newPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    if (newPassword && currentPassword && newPassword === currentPassword) {
      errors.newPassword = 'New password must be different from current password';
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };
  // Handle password change form submission
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setFormErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      general: ''
    });
    if (!validatePasswordForm()) {
      return;
    }
    if (!currentUser) {
      setFormErrors({
        ...formErrors,
        general: 'You must be logged in to change your password'
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email!, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      const userDocRef = doc(db, 'users', currentUser.uid);
      await setDoc(userDocRef, {
        lastPasswordChange: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
      setSuccessMessage('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setFormErrors({
          ...formErrors,
          currentPassword: 'Incorrect password'
        });
      } else if (error.code === 'auth/too-many-requests') {
        setFormErrors({
          ...formErrors,
          general: 'Too many attempts. Please try again later.'
        });
      } else if (error.code === 'auth/requires-recent-login') {
        setFormErrors({
          ...formErrors,
          general: 'For security reasons, please log out and log back in before changing your password.'
        });
      } else {
        setFormErrors({
          ...formErrors,
          general: `Error: ${error.message || 'Failed to update password'}`
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  // Handle delete account button click
  const handleDeleteAccount = () => {
    setIsDeleteAccountModalOpen(true);
  };
  return <main className="pb-6" data-id="element-2975">
      <div className="max-w-7xl mx-auto px-4" data-id="element-2976">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-2977">
          <div className="mb-4 md:mb-0" data-id="element-2978">
            <h1 className="text-2xl sm:text-3xl font-bold" data-id="element-2979">Account Security</h1>
          </div>
        </div>

        {/* Show global settings error if present */}
        {settingsError && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-red-900/20 border border-red-800/30 text-red-300' : 'bg-red-50 border border-red-100 text-red-800'} flex items-start`} data-id="element-2980">
            <AlertCircleIcon className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-red-500" data-id="element-2981" />
            <span data-id="element-2982">{settingsError}</span>
          </div>}

        {/* Show global success message if present */}
        {successMessage && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-800/30 text-green-300' : 'bg-green-50 border border-green-100 text-green-800'} flex items-start`} data-id="element-2983">
            <CheckCircleIcon className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-green-500" data-id="element-2984" />
            <span data-id="element-2985">{successMessage}</span>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2986">
          {/* Password Change Section */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2`} data-id="element-2987">
            <div className="flex items-center mb-4" data-id="element-2988">
              <LockIcon className="h-5 w-5 text-deep-pink mr-2" aria-hidden="true" data-id="element-2989" />
              <h2 className="text-xl font-serif font-semibold" data-id="element-2990">
                Change Password
              </h2>
            </div>

            {/* OAuth User Message */}
            {!hasPasswordProvider && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-yellow-900/20 border border-yellow-800/30' : 'bg-yellow-50 border border-yellow-100'} flex items-start`} data-id="element-2991">
                <InfoIcon className={`h-5 w-5 mt-0.5 mr-2 flex-shrink-0 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} data-id="element-2992" />
                <div data-id="element-2993">
                  <p className={`text-sm font-medium ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`} data-id="element-2994">
                    You signed in with Google or Apple
                  </p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-yellow-300/80' : 'text-yellow-700'}`} data-id="element-2995">
                    Password changes are not available for social sign-in
                    accounts. Your account security is managed by your social
                    provider.
                  </p>
                </div>
              </div>}

            {/* Password Change Form - Only show for email/password users */}
            {hasPasswordProvider && <>
                {/* Success message */}
                {successMessage && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-800/30 text-green-300' : 'bg-green-50 border border-green-100 text-green-800'} flex items-start`} data-id="element-2996">
                    <CheckCircleIcon className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-green-500" data-id="element-2997" />
                    <span data-id="element-2998">{successMessage}</span>
                  </div>}

                {/* General error message */}
                {formErrors.general && <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-red-900/20 border border-red-800/30 text-red-300' : 'bg-red-50 border border-red-100 text-red-800'} flex items-start`} data-id="element-2999">
                    <AlertCircleIcon className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-red-500" data-id="element-3000" />
                    <span data-id="element-3001">{formErrors.general}</span>
                  </div>}

                <form className="space-y-4" onSubmit={handlePasswordChange} data-id="element-3002">
                  {/* Current Password */}
                  <div data-id="element-3003">
                    <label htmlFor="current-password" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3004">
                      Current Password
                    </label>
                    <div className="relative" data-id="element-3005">
                      <input type={showCurrentPassword ? 'text' : 'password'} id="current-password" className={`w-full px-4 py-2 rounded-lg ${formErrors.currentPassword ? darkMode ? 'border-red-500 bg-red-900/10' : 'border-red-500 bg-red-50' : darkMode ? 'bg-navy-900/50 border-navy-700' : 'bg-white border-gray-300'} ${darkMode ? 'text-white' : 'text-gray-900'} focus:ring-deep-pink focus:border-deep-pink border`} placeholder="Enter your current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} disabled={isSubmitting} data-id="element-3006" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowCurrentPassword(!showCurrentPassword)} aria-label={showCurrentPassword ? 'Hide password' : 'Show password'} data-id="element-3007">
                        {showCurrentPassword ? <EyeOffIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3008" /> : <EyeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3009" />}
                      </button>
                    </div>
                    {formErrors.currentPassword && <p className={`mt-1 text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3010">
                        {formErrors.currentPassword}
                      </p>}
                  </div>

                  {/* New Password */}
                  <div data-id="element-3011">
                    <label htmlFor="new-password" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3012">
                      New Password
                    </label>
                    <div className="relative" data-id="element-3013">
                      <input type={showNewPassword ? 'text' : 'password'} id="new-password" className={`w-full px-4 py-2 rounded-lg ${formErrors.newPassword ? darkMode ? 'border-red-500 bg-red-900/10' : 'border-red-500 bg-red-50' : darkMode ? 'bg-navy-900/50 border-navy-700' : 'bg-white border-gray-300'} ${darkMode ? 'text-white' : 'text-gray-900'} focus:ring-deep-pink focus:border-deep-pink border`} placeholder="Enter your new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} disabled={isSubmitting} data-id="element-3014" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowNewPassword(!showNewPassword)} aria-label={showNewPassword ? 'Hide password' : 'Show password'} data-id="element-3015">
                        {showNewPassword ? <EyeOffIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3016" /> : <EyeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3017" />}
                      </button>
                    </div>
                    {formErrors.newPassword && <p className={`mt-1 text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3018">
                        {formErrors.newPassword}
                      </p>}

                    {/* Password Strength Indicator */}
                    {newPassword.length > 0 && <div className="mt-3" data-id="element-3019">
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-3020">
                          <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{
                      width: `${passwordStrength.score / 4 * 100}%`
                    }} data-id="element-3021"></div>
                        </div>
                        <div className="mt-1" data-id="element-3022">
                          <span className={`text-xs font-medium ${passwordStrength.score <= 1 ? 'text-red-500' : passwordStrength.score === 2 ? 'text-orange-500' : passwordStrength.score === 3 ? 'text-yellow-500' : 'text-green-500'}`} data-id="element-3023">
                            {passwordStrength.label}
                          </span>
                        </div>
                      </div>}

                    {/* Password Requirements Feedback */}
                    <div className={`mt-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3024">
                      <p className="mb-1" data-id="element-3025">Password must include:</p>
                      <ul className="space-y-1" data-id="element-3026">
                        <li className="flex items-center" data-id="element-3027">
                          {passwordRequirements.length ? <CheckIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-green-400' : 'text-green-500'}`} data-id="element-3028" /> : <XIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3029" />}
                          At least 8 characters
                        </li>
                        <li className="flex items-center" data-id="element-3030">
                          {passwordRequirements.lowercase ? <CheckIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-green-400' : 'text-green-500'}`} data-id="element-3031" /> : <XIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3032" />}
                          Lowercase letters (a-z)
                        </li>
                        <li className="flex items-center" data-id="element-3033">
                          {passwordRequirements.uppercase ? <CheckIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-green-400' : 'text-green-500'}`} data-id="element-3034" /> : <XIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3035" />}
                          Uppercase letters (A-Z)
                        </li>
                        <li className="flex items-center" data-id="element-3036">
                          {passwordRequirements.number ? <CheckIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-green-400' : 'text-green-500'}`} data-id="element-3037" /> : <XIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3038" />}
                          Numbers (0-9)
                        </li>
                        <li className="flex items-center" data-id="element-3039">
                          {passwordRequirements.special ? <CheckIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-green-400' : 'text-green-500'}`} data-id="element-3040" /> : <XIcon className={`h-3 w-3 mr-1 ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3041" />}
                          Special characters (!@#$%^&*)
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Confirm New Password */}
                  <div data-id="element-3042">
                    <label htmlFor="confirm-password" className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3043">
                      Confirm New Password
                    </label>
                    <div className="relative" data-id="element-3044">
                      <input type={showConfirmPassword ? 'text' : 'password'} id="confirm-password" className={`w-full px-4 py-2 rounded-lg ${formErrors.confirmPassword || confirmPassword && newPassword !== confirmPassword ? darkMode ? 'border-red-500 bg-red-900/10' : 'border-red-500 bg-red-50' : darkMode ? 'bg-navy-900/50 border-navy-700' : 'bg-white border-gray-300'} ${darkMode ? 'text-white' : 'text-gray-900'} focus:ring-deep-pink focus:border-deep-pink border`} placeholder="Confirm your new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} disabled={isSubmitting} data-id="element-3045" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} data-id="element-3046">
                        {showConfirmPassword ? <EyeOffIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3047" /> : <EyeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3048" />}
                      </button>
                    </div>
                    {(formErrors.confirmPassword || confirmPassword && newPassword !== confirmPassword) && <p className={`mt-1 text-sm ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-3049">
                        {formErrors.confirmPassword || 'Passwords do not match'}
                      </p>}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2" data-id="element-3050">
                    <button type="submit" disabled={isSubmitting} className={`px-4 py-2 rounded-lg flex items-center bg-transparent border-2 border-green-500 text-green-500 hover:bg-green-500/10 transition-colors ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`} data-id="element-3051">
                      {isSubmitting ? <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3052">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3053"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3054"></path>
                          </svg>
                          Updating...
                        </> : <>
                          <SaveIcon className="h-4 w-4 mr-2" data-id="element-3055" />
                          Update Password
                        </>}
                    </button>
                  </div>
                </form>
              </>}
          </div>

          {/* Two-Factor Authentication - Hidden when ENABLE_2FA is false */}
          {ENABLE_2FA && <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-1`} data-id="element-3056">
              <div className="flex items-center mb-4" data-id="element-3057">
                <KeyIcon className="h-5 w-5 text-deep-pink mr-2" aria-hidden="true" data-id="element-3058" />
                <h2 className="text-xl font-serif font-semibold" data-id="element-3059">
                  Two-Factor Authentication
                </h2>
              </div>

              <div className={`p-4 rounded-lg mb-4 ${twoFactorEnabled ? darkMode ? 'bg-green-900/20 border border-green-800/30' : 'bg-green-50 border border-green-100' : darkMode ? 'bg-yellow-900/20 border border-yellow-800/30' : 'bg-yellow-50 border border-yellow-100'}`} data-id="element-3060">
                <div className="flex items-start" data-id="element-3061">
                  {twoFactorEnabled ? <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" aria-hidden="true" data-id="element-3062" /> : <AlertCircleIcon className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" aria-hidden="true" data-id="element-3063" />}
                  <div data-id="element-3064">
                    <p className={`text-sm font-medium ${twoFactorEnabled ? darkMode ? 'text-green-300' : 'text-green-800' : darkMode ? 'text-yellow-300' : 'text-yellow-800'}`} data-id="element-3065">
                      {twoFactorEnabled ? 'Two-factor authentication is enabled' : 'Two-factor authentication is disabled'}
                    </p>
                    <p className={`text-xs mt-1 ${twoFactorEnabled ? darkMode ? 'text-green-300/70' : 'text-green-700' : darkMode ? 'text-yellow-300/70' : 'text-yellow-700'}`} data-id="element-3066">
                      {twoFactorEnabled ? 'Your account is protected with an additional layer of security.' : 'Enable 2FA to add an extra layer of security to your account.'}
                    </p>
                  </div>
                </div>
              </div>

              <button onClick={() => {
            console.log('2FA button clicked, current state:', twoFactorEnabled);
            updateSecuritySetting('twoFactorEnabled', !twoFactorEnabled);
          }} disabled={isLoadingSettings || isSavingSettings.twoFactorEnabled} className={`w-full px-4 py-2 rounded-lg flex items-center justify-center mb-4 ${isLoadingSettings || isSavingSettings.twoFactorEnabled ? 'opacity-70 cursor-not-allowed' : twoFactorEnabled ? darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700' : darkMode ? 'bg-deep-pink hover:bg-burgundy text-white' : 'bg-deep-pink hover:bg-burgundy text-white'}`} data-id="element-3067">
                {isLoadingSettings ? <svg className="animate-spin h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3068">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3069"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3070"></path>
                  </svg> : isSavingSettings.twoFactorEnabled ? <svg className="animate-spin h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3071">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3072"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3073"></path>
                  </svg> : twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
              </button>

              <div className={`flex items-start mt-2 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3074">
                <InfoIcon className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-deep-pink" data-id="element-3075" />
                <p className="text-xs" data-id="element-3076">
                  Enable 2FA to receive a one-time code via authenticator app
                  when signing in. This adds an extra layer of protection
                  against unauthorized access.
                </p>
              </div>

              {twoFactorEnabled && <button onClick={generateBackupCodes} className={`w-full px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} disabled={generatingBackupCodes} data-id="element-3077">
                  {generatingBackupCodes ? <svg className="animate-spin h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3078">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3079"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3080"></path>
                    </svg> : <>
                      <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-3081" />
                      Regenerate Backup Codes
                    </>}
                </button>}
            </div>}

          {/* Security Settings */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-2`} data-id="element-3082">
            <h2 className="text-xl font-serif font-semibold mb-4" data-id="element-3083">
              Security Settings
            </h2>

            <div className="space-y-4" data-id="element-3084">
              {/* Security Alerts */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-3085">
                <div data-id="element-3086">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3087">
                    Security Alerts
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-3088">
                    Receive email alerts for suspicious activity
                  </p>
                </div>
                <button onClick={() => updateSecuritySetting('securityAlerts', !securityAlerts)} disabled={isLoadingSettings || isSavingSettings.securityAlerts} className="focus:outline-none relative" aria-pressed={securityAlerts} data-id="element-3089">
                  {isLoadingSettings ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3090">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3091">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3092"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3093"></path>
                      </svg>
                    </div> : isSavingSettings.securityAlerts ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3094">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3095">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3096"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3097"></path>
                      </svg>
                    </div> : securityAlerts ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-3098">
                      <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-3099"></div>
                    </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-3100">
                      <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-3101"></div>
                    </div>}
                </button>
              </div>

              {/* Email Notifications */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-3102">
                <div data-id="element-3103">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3104">
                    Email Notifications
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-3105">
                    Receive email when someone logs into your account
                  </p>
                </div>
                <button onClick={() => updateSecuritySetting('emailNotifications', !emailNotifications)} disabled={isLoadingSettings || isSavingSettings.emailNotifications} className="focus:outline-none" aria-pressed={emailNotifications} data-id="element-3106">
                  {isLoadingSettings ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3107">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3108">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3109"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3110"></path>
                      </svg>
                    </div> : isSavingSettings.emailNotifications ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3111">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3112">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3113"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3114"></path>
                      </svg>
                    </div> : emailNotifications ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-3115">
                      <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-3116"></div>
                    </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-3117">
                      <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-3118"></div>
                    </div>}
                </button>
              </div>

              {/* Auto Sign-Out - FIXED */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-3119">
                <div data-id="element-3120">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3121">
                    Auto Sign-Out (Inactive Devices)
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-3122">
                    Sign out from inactive devices automatically after 30 days
                    of inactivity
                  </p>
                </div>
                <button onClick={() => updateSecuritySetting('autoSignOut', !autoSignOut)} disabled={isLoadingSettings || isSavingSettings.autoSignOut} className="focus:outline-none" aria-pressed={autoSignOut} data-id="element-3123">
                  {isLoadingSettings ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3124">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3125">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3126"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3127"></path>
                      </svg>
                    </div> : isSavingSettings.autoSignOut ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3128">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3129">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3130"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3131"></path>
                      </svg>
                    </div> : autoSignOut ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-3132">
                      <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-3133"></div>
                    </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-3134">
                      <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-3135"></div>
                    </div>}
                </button>
              </div>

              {/* Password Change Reminder - FIXED */}
              <div className={`flex items-center justify-between p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-3136">
                <div data-id="element-3137">
                  <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3138">
                    Password Change Reminder
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-3139">
                    Remind me to change my password every 90 days
                  </p>
                </div>
                <button onClick={() => updateSecuritySetting('passwordChangeReminder', !passwordChangeReminder)} disabled={isLoadingSettings || isSavingSettings.passwordChangeReminder} className="focus:outline-none" aria-pressed={passwordChangeReminder} data-id="element-3140">
                  {isLoadingSettings ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3141">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3142">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3143"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3144"></path>
                      </svg>
                    </div> : isSavingSettings.passwordChangeReminder ? <div className="h-6 w-11 flex items-center justify-center" data-id="element-3145">
                      <svg className="animate-spin h-5 w-5 text-deep-pink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3146">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3147"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3148"></path>
                      </svg>
                    </div> : passwordChangeReminder ? <div className="h-6 w-11 bg-green-500 rounded-full relative" data-id="element-3149">
                      <div className="h-5 w-5 bg-white rounded-full absolute right-0.5 top-0.5 transition-transform" data-id="element-3150"></div>
                    </div> : <div className={`h-6 w-11 rounded-full relative ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-3151">
                      <div className="h-5 w-5 bg-white rounded-full absolute left-0.5 top-0.5 transition-transform" data-id="element-3152"></div>
                    </div>}
                </button>
              </div>
            </div>
          </div>

          {/* Connected Accounts - Coming Soon */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-1`} data-id="element-3153">
            <h2 className="text-xl font-serif font-semibold mb-4" data-id="element-3154">
              Connected Accounts
            </h2>
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50 border border-navy-700' : 'bg-gray-50 border border-gray-200'} text-center`} data-id="element-3155">
              <InfoIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3156" />
              <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3157">
                Coming Soon
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3158">
                Connect your Google, Facebook, or Apple account to sign in
                faster. This feature will be available in a future update.
              </p>
            </div>
          </div>

          {/* Danger Zone - Delete Account Section */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-6 lg:col-span-3 mt-4 border ${darkMode ? 'border-red-800/30' : 'border-red-200'}`} data-id="element-3159">
            <h2 className="text-xl font-serif font-semibold mb-2 text-red-500" data-id="element-3160">
              Danger Zone
            </h2>
            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3161">
              Permanently delete your Maestro Connect account and all associated
              data.
            </p>
            <button onClick={handleDeleteAccount} className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-navy-900/50 text-red-400 hover:bg-navy-800' : 'bg-gray-100 text-red-600 hover:bg-gray-200'} transition-colors`} data-id="element-3162">
              Delete Account
            </button>
          </div>
        </div>

        {/* Two-Factor Authentication Enrollment Modal */}
        {enrolling2FA && <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-3163">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3164">
              {/* Background overlay */}
              <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={() => closeEnrollment()} data-id="element-3165"></div>

              {/* Modal panel */}
              <div className={`inline-block align-bottom ${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`} data-id="element-3166">
                <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} px-4 pt-5 pb-4 sm:p-6 sm:pb-4`} data-id="element-3167">
                  <div className="sm:flex sm:items-start" data-id="element-3168">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-3169">
                      <KeyIcon className="h-6 w-6 text-deep-pink" aria-hidden="true" data-id="element-3170" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-3171">
                      <h3 className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} id="modal-title" data-id="element-3172">
                        {verificationStep === 1 && 'Set up Two-Factor Authentication'}
                        {verificationStep === 2 && 'Verify Authentication Code'}
                        {verificationStep === 3 && 'Save Your Backup Codes'}
                      </h3>
                      <div className="mt-2" data-id="element-3173">
                        {verificationStep === 1 && <div data-id="element-3174">
                            {verificationError && <div className={`p-3 mb-4 rounded-lg ${darkMode ? 'bg-red-900/20 border border-red-800/30 text-red-300' : 'bg-red-50 border border-red-100 text-red-800'} flex items-start`} data-id="element-3175">
                                <AlertCircleIcon className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-red-500" data-id="element-3176" />
                                <span data-id="element-3177">{verificationError}</span>
                              </div>}
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'} mb-4`} data-id="element-3178">
                              Scan this QR code with your authenticator app
                              (Google Authenticator, Authy, etc.) to enable
                              two-factor authentication.
                            </p>
                            <div className="flex justify-center my-6" data-id="element-3179">
                              {qrCodeUrl ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-white' : 'bg-gray-100'}`} data-id="element-3180">
                                  <QRCodeSVG value={qrCodeUrl} size={200} data-id="element-3181" />
                                </div> : <div className="flex flex-col items-center justify-center" data-id="element-3182">
                                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-pink" data-id="element-3183"></div>
                                  <p className="mt-2 text-sm text-gray-400" data-id="element-3184">
                                    Loading QR code...
                                  </p>
                                </div>}
                            </div>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'} mb-2`} data-id="element-3185">
                              Can't scan the QR code? Set up with this code
                              instead:
                            </p>
                            {totpSecret ? <div className="flex justify-center items-center mb-4" data-id="element-3186">
                                <code className={`font-mono text-lg p-2 ${darkMode ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-800'}`} data-id="element-3187">
                                  {totpSecret.secretKey}
                                </code>
                                <button onClick={() => {
                          navigator.clipboard.writeText(totpSecret.secretKey);
                          setSuccessMessage('Secret code copied to clipboard!');
                          setTimeout(() => setSuccessMessage(''), 3000);
                        }} className={`ml-2 p-2 ${darkMode ? 'bg-navy-700 hover:bg-navy-600' : 'bg-gray-200 hover:bg-gray-300'} rounded`} title="Copy to clipboard" data-id="element-3188">
                                  <ClipboardIcon className="h-5 w-5" data-id="element-3189" />
                                </button>
                              </div> : <div className="flex justify-center mb-4" data-id="element-3190">
                                <div className={`font-mono text-lg p-2 ${darkMode ? 'bg-navy-900 text-gray-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-3191">
                                  Loading...
                                </div>
                              </div>}
                            <button onClick={() => setVerificationStep(2)} disabled={!totpSecret || !qrCodeUrl} className={`w-full px-4 py-2 rounded-lg flex items-center justify-center ${!totpSecret || !qrCodeUrl ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? 'bg-deep-pink hover:bg-burgundy' : 'bg-deep-pink hover:bg-burgundy'} text-white transition-colors`} data-id="element-3192">
                              Continue
                            </button>
                          </div>}

                        {verificationStep === 2 && <div data-id="element-3193">
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'} mb-4`} data-id="element-3194">
                              Enter the 6-digit verification code from your
                              authenticator app to complete the setup.
                            </p>
                            {verificationError && <div className={`p-3 mb-4 rounded-lg ${darkMode ? 'bg-red-900/20 border border-red-800/30 text-red-300' : 'bg-red-50 border border-red-100 text-red-800'} flex items-start`} data-id="element-3195">
                                <AlertCircleIcon className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-red-500" data-id="element-3196" />
                                <span data-id="element-3197">{verificationError}</span>
                              </div>}
                            <div className="mb-4" data-id="element-3198">
                              <input type="text" value={verificationCode} onChange={e => {
                          // Only allow digits and limit to 6 characters
                          const value = e.target.value.replace(/[^0-9]/g, '').substring(0, 6);
                          setVerificationCode(value);
                        }} className={`w-full px-4 py-2 text-center text-2xl tracking-widest font-mono rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-deep-pink focus:border-deep-pink border`} placeholder="000000" maxLength={6} autoFocus data-id="element-3199" />
                            </div>
                            <div className="flex space-x-3" data-id="element-3200">
                              <button onClick={() => setVerificationStep(1)} className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-colors`} data-id="element-3201">
                                Back
                              </button>
                              <button onClick={verifyTotpAndEnroll} disabled={verificationCode.length !== 6 || isSavingSettings.twoFactorEnabled} className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-deep-pink hover:bg-burgundy' : 'bg-deep-pink hover:bg-burgundy'} text-white transition-colors ${verificationCode.length !== 6 || isSavingSettings.twoFactorEnabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-3202">
                                {isSavingSettings.twoFactorEnabled ? <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3203">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3204"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3205"></path>
                                  </svg> : 'Verify'}
                              </button>
                            </div>
                          </div>}

                        {verificationStep === 3 && <div data-id="element-3206">
                            <div className={`p-3 mb-4 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-800/30 text-green-300' : 'bg-green-50 border border-green-100 text-green-800'} flex items-start`} data-id="element-3207">
                              <CheckCircleIcon className="h-5 w-5 mt-0.5 mr-2 flex-shrink-0 text-green-500" data-id="element-3208" />
                              <span data-id="element-3209">
                                Two-factor authentication has been successfully
                                enabled!
                              </span>
                            </div>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'} mb-4`} data-id="element-3210">
                              <strong data-id="element-3211">Important:</strong> Save these backup
                              codes in a safe place. You'll need them if you
                              lose access to your authenticator app.
                            </p>
                            <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50 border border-navy-700' : 'bg-gray-50 border border-gray-200'} mb-4 font-mono text-sm`} data-id="element-3212">
                              {backupCodes.map((code, index) => <div key={code.id} className={`py-1 ${index < backupCodes.length - 1 ? darkMode ? 'border-b border-navy-700' : 'border-b border-gray-200' : ''}`} data-id="element-3213">
                                  {code.code}
                                </div>)}
                            </div>
                            <div className="flex flex-wrap gap-3 mb-4" data-id="element-3214">
                              <button onClick={copyBackupCodes} className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-colors`} data-id="element-3215">
                                <CopyIcon className="h-4 w-4 mr-2" data-id="element-3216" />
                                Copy
                              </button>
                              <button onClick={downloadBackupCodes} className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-colors`} data-id="element-3217">
                                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3218" />
                                Download
                              </button>
                            </div>
                            <button onClick={closeEnrollment} className={`w-full px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-deep-pink hover:bg-burgundy' : 'bg-deep-pink hover:bg-burgundy'} text-white transition-colors`} data-id="element-3219">
                              Done
                            </button>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>
                {verificationStep !== 3 && <div className={`${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`} data-id="element-3220">
                    <button type="button" className={`mt-3 w-full inline-flex justify-center rounded-md border ${darkMode ? 'border-gray-600 bg-navy-700 text-gray-300 hover:bg-navy-600' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'} shadow-sm px-4 py-2 text-base font-medium focus:outline-none sm:mt-0 sm:w-auto sm:text-sm`} onClick={closeEnrollment} data-id="element-3221">
                      Cancel
                    </button>
                  </div>}
              </div>
            </div>
          </div>}

        {/* Backup Codes Modal */}
        {showBackupCodes && !enrolling2FA && <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-3222">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3223">
              {/* Background overlay */}
              <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm transition-opacity" aria-hidden="true" data-id="element-3224"></div>

              {/* Modal panel */}
              <div className={`inline-block align-bottom ${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`} data-id="element-3225">
                <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} px-4 pt-5 pb-4 sm:p-6 sm:pb-4`} data-id="element-3226">
                  <div className="sm:flex sm:items-start" data-id="element-3227">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-3228">
                      <KeyIcon className="h-6 w-6 text-deep-pink" aria-hidden="true" data-id="element-3229" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-3230">
                      <h3 className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} id="modal-title" data-id="element-3231">
                        Your Backup Codes
                      </h3>
                      <div className="mt-2" data-id="element-3232">
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'} mb-4`} data-id="element-3233">
                          <strong data-id="element-3234">Important:</strong> Save these backup codes in
                          a safe place. You'll need them if you lose access to
                          your authenticator app.
                        </p>
                        <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50 border border-navy-700' : 'bg-gray-50 border border-gray-200'} mb-4 font-mono text-sm`} data-id="element-3235">
                          {backupCodes.map((code, index) => <div key={code.id} className={`py-1 ${index < backupCodes.length - 1 ? darkMode ? 'border-b border-navy-700' : 'border-b border-gray-200' : ''}`} data-id="element-3236">
                              {code.code}
                            </div>)}
                        </div>
                        <div className="flex flex-wrap gap-3 mb-4" data-id="element-3237">
                          <button onClick={copyBackupCodes} className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-colors`} data-id="element-3238">
                            <CopyIcon className="h-4 w-4 mr-2" data-id="element-3239" />
                            Copy
                          </button>
                          <button onClick={downloadBackupCodes} className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-colors`} data-id="element-3240">
                            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-3241" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`} data-id="element-3242">
                  <button type="button" className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${darkMode ? 'bg-deep-pink hover:bg-burgundy' : 'bg-deep-pink hover:bg-burgundy'} text-base font-medium text-white focus:outline-none sm:ml-3 sm:w-auto sm:text-sm`} onClick={() => setShowBackupCodes(false)} data-id="element-3243">
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Disable 2FA Confirmation Dialog */}
        {showDisableConfirmation && <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-3244">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3245">
              {/* Background overlay */}
              <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm transition-opacity" aria-hidden="true" data-id="element-3246"></div>

              {/* Modal panel */}
              <div className={`inline-block align-bottom ${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`} data-id="element-3247">
                <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} px-4 pt-5 pb-4 sm:p-6 sm:pb-4`} data-id="element-3248">
                  <div className="sm:flex sm:items-start" data-id="element-3249">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-3250">
                      <AlertCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" data-id="element-3251" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-3252">
                      <h3 className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} id="modal-title" data-id="element-3253">
                        Disable Two-Factor Authentication
                      </h3>
                      <div className="mt-2" data-id="element-3254">
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-3255">
                          Are you sure you want to disable two-factor
                          authentication? This will make your account less
                          secure.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`} data-id="element-3256">
                  <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm" onClick={disableTwoFactorAuth} disabled={isSavingSettings.twoFactorEnabled} data-id="element-3257">
                    {isSavingSettings.twoFactorEnabled ? <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3258">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3259"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3260"></path>
                      </svg> : 'Disable 2FA'}
                  </button>
                  <button type="button" className={`mt-3 w-full inline-flex justify-center rounded-md border ${darkMode ? 'border-gray-600 bg-navy-700 text-gray-300 hover:bg-navy-600' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'} shadow-sm px-4 py-2 text-base font-medium focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`} onClick={() => setShowDisableConfirmation(false)} data-id="element-3261">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Use the DeleteAccountModal component */}
        <DeleteAccountModal isOpen={isDeleteAccountModalOpen} onClose={() => setIsDeleteAccountModalOpen(false)} data-id="element-3262" />
      </div>
    </main>;
};